import { CSSProperties, FC } from "react";

import styled from "styled-components";

/**
 * FavoriteHeart — iOS-safe heart toggle.
 *
 * iOS WebKit does NOT repaint an SVG `fill` that is changed in place — whether
 * the change comes from a presentation attribute, an inline `style`, or a
 * cascaded CSS rule from a parent className. The fill only updates on the next
 * reflow, which is why the favourite heart used to flip "on scroll" instead of
 * on tap.
 *
 * We stack two STATIC hearts — an outline and a white-filled one — and toggle
 * only the filled heart's `opacity`. (Toggling opacity rather than mutating a
 * `fill` also sidesteps any SVG-fill repaint quirk.) The actual cause of the
 * "flips only on scroll" bug turned out to be a stuck `:hover` on touch — see
 * the `@media (hover: hover)` guards on the parent buttons (AlbumComponent's
 * ActionBtn and Catalog SongStack) — so no forced compositing layer is needed
 * here; a plain opacity toggle repaints fine.
 *
 * Everything that matters is inline-styled on the SVGs so a parent button's
 * `svg { ... }` rules can't override the colours/positioning.
 *
 * Desktop hover-preview (fill the heart on hover) is layered on by the parent:
 *   @media (hover: hover) { &:hover .fav-heart-fill { opacity: 1 !important; } }
 * (`!important` beats the inline opacity; `hover: hover` keeps touch devices
 *  from sticking the filled state after a tap.)
 */

const HEART_PATH =
  "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z";

const Wrap = styled.span<{ $size: number }>`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

const baseSvg: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
};

interface IFavoriteHeart {
  active: boolean;
  size?: number;
  /** Outline (un-favourited) stroke colour. Defaults to currentColor. */
  outlineColor?: string;
  /** Filled (favourited) colour. Defaults to white. */
  fillColor?: string;
  /** Stroke width for both hearts. */
  strokeWidth?: number;
  className?: string;
}

export const FavoriteHeart: FC<IFavoriteHeart> = ({
  active,
  size = 18,
  outlineColor = "currentColor",
  fillColor = "#ffffff",
  strokeWidth = 2,
  className,
}) => (
  <Wrap $size={size} className={className}>
    {/* Outline — always visible underneath */}
    <svg
      viewBox="0 0 24 24"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ ...baseSvg, fill: "none", stroke: outlineColor }}
    >
      <path d={HEART_PATH} />
    </svg>
    {/* Filled — opacity-toggled on top (static fill, never mutated) */}
    <svg
      className="fav-heart-fill"
      viewBox="0 0 24 24"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        ...baseSvg,
        fill: fillColor,
        stroke: fillColor,
        opacity: active ? 1 : 0,
        transition: "opacity 0.2s ease",
      }}
    >
      <path d={HEART_PATH} />
    </svg>
  </Wrap>
);

export default FavoriteHeart;
